import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadLinksPreset } from "@tsparticles/preset-links";
import { loadExternalGrabInteraction } from "@tsparticles/interaction-external-grab";
import { loadExternalPushInteraction } from "@tsparticles/interaction-external-push";

const fullSizeStyle = { width: "100%", height: "100%" };

function prefersReducedMotion() {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

/*
 * Opções no formato do tsparticles v3 (engine 3.9.1). Três armadilhas que já custaram caro aqui,
 * todas silenciosas — a engine ignora sem erro:
 *
 * 1. density usa width/height (não "area", que era da v2);
 * 2. resize é objeto { enable } (não boolean, que era da v2);
 * 3. o preset "links" NÃO traz interação com o mouse. onHover "grab" e onClick "push" só
 *    funcionam com os plugins interaction-external-grab / -push carregados abaixo. Eles
 *    precisam estar na MESMA versão da engine (checkVersion recusa diferença).
 */
function ParticlesBackground({
  className = "",
  color = "rgba(255,255,255,0.55)",
  linkColor,
  linkOpacity = 0.2,
  // Alcance curto de propósito: com 150px as ligações entre pontos distantes viravam riscos longos e
  // quase horizontais, que de longe parecem um degradê sujo no fundo.
  linkDistance = 110,
  linkWidth = 0.6,
  speed = 1.2,
  quantity = 60,
  background = "transparent",
  enableClick = true,
}) {
  const [ready, setReady] = useState(false);
  const [reducedMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadLinksPreset(engine);
      await loadExternalGrabInteraction(engine);
      await loadExternalPushInteraction(engine);
    }).then(() => setReady(true));
  }, []);

  const options = useMemo(() => ({
    preset: "links",
    background: { color: { value: background } },
    fullScreen: { enable: false },
    fpsLimit: 60,
    detectRetina: true,
    interactivity: {
      detectsOn: "canvas",
      events: {
        // Ao passar o mouse, os pontos próximos se ligam ao cursor.
        onHover: { enable: true, mode: "grab" },
        onClick: { enable: enableClick, mode: "push" },
        resize: { enable: true },
      },
      modes: {
        grab: { distance: Math.round(linkDistance * 1.15), links: { opacity: 0.45 } },
        push: { quantity: 2 },
      },
    },
    particles: {
      color: { value: color },
      links: { color: linkColor || color, distance: linkDistance, enable: true, opacity: linkOpacity, width: linkWidth },
      // Com "reduzir movimento" ligado no sistema operacional, a rede fica parada mas continua
      // respondendo ao mouse.
      move: { enable: !reducedMotion, speed, outModes: { default: "out" } },
      // quantity vale para uma área de referência 1920x1080 e escala com o tamanho real do canvas.
      number: { density: { enable: true, width: 1920, height: 1080 }, value: quantity },
      opacity: { value: { min: 0.15, max: 0.55 } },
      shape: { type: "circle" },
      size: { value: { min: 1, max: 3 } },
    },
  }), [background, color, enableClick, linkColor, linkDistance, linkOpacity, linkWidth, quantity, reducedMotion, speed]);

  const particlesLoaded = useCallback(() => {}, []);

  if (!ready) return null;

  return (
    <Particles
      id="particles-background"
      options={options}
      particlesLoaded={particlesLoaded}
      className={className}
      style={fullSizeStyle}
    />
  );
}

export default memo(ParticlesBackground);
